import { getS3UploadUrl, uploadFileToS3 } from "@/api/leave-requests";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import AmPmDropdown from "@/components/form/AmPmDropdown";
import DatePicker from "@/components/form/DatePicker";
import FileUploadInput from "@/components/form/FileUploadInput";
import LeaveTypeDropdown from "@/components/form/LeaveTypeDropdown";
import MultiLineTextInput from "@/components/form/MultiLineTextInput";
import NumberInput from "@/components/form/NumberInput";
import TextInput from "@/components/form/TextInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrentUser } from "@/context/UserContext";
import { addLeaveRequest, fetchLeaveRequestsOfUser, fetchUserLeaveRequestsCount, resetUserFilters } from "@/features/leaveRequests/leaveRequestSlice";
import { useToast } from "@/hooks/use-toast";
import { CreateLeaveRequestArgs } from "@/models/leave-requests.model";
import { calculateDaysOfLeave } from "@/utils/date-format";
import { Loader2 } from "lucide-react";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}
function RequestLeaveDialog({ isOpen, setIsOpen }: Props) {
  const { toast } = useToast();
  const { userPagination } = useAppSelector((state) => state.leaveRequests);
  const dispatch = useAppDispatch();


  const { currentUser } = useCurrentUser();
  const [leaveType, setLeaveType] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [days, setDays] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [startDateIsAm, setStartDateIsAm] = useState<boolean>(true);
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [endDateIsAm, setEndDateIsAm] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);

  const [error, setError] = useState<string>("");
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    console.log(currentUser);
    if (!currentUser) {
      return;
    }
    if (!isValid()) {
      return;
    }
    try {
      setIsLoading(true);
      const fileKey = file ? await uploadFile(file) : "";
      const createArgs: CreateLeaveRequestArgs = {
        userId: currentUser.id,
        leaveType,
        fromDate: startDate,
        fromDateIsAm: startDateIsAm,
        toDate: endDate,
        toDateIsAm: endDateIsAm,
        reason,
        fileUrl: fileKey,
        days,
      };
      // create leave request
      await dispatch(addLeaveRequest(createArgs)).unwrap();
      setIsOpen(false);
      clearForm();
      toast({
        title: "Leave Request Created!",
        description: `Approval has to be made by your employer.`,
      });
      console.log("restting user filters");
      dispatch(resetUserFilters())
      await dispatch(fetchLeaveRequestsOfUser({
        userId: currentUser.id,
        page: 1, // Reset to first page
        limit: 7
      }));
      await dispatch(fetchUserLeaveRequestsCount(currentUser.id));
      setIsLoading(false);


    } catch (error: any) {
      setIsLoading(false);
      console.error("error", error);

      toast({
        title: error.message || "Error",
        description: error?.error || "Failed to create leave request",
        variant: "destructive",
      })
    }
  };

  const clearForm = () => {
    setLeaveType("");
    setReason("");
    setDays(0);
    setStartDate(new Date());
  };

  const uploadFile = async (file: File) => {
    console.log("file", file);
    const urlRes = await getS3UploadUrl(file?.name || "", file?.type || "");
    const { uploadKey, uploadUrl } = urlRes;
    const res = await uploadFileToS3(file, uploadUrl);
    return uploadKey;
  }

  const isValid = () => {
    if (leaveType.length === 0) {
      setError("Please select a leave type");
      return false;
    }
    if (startDate === undefined) {
      setError("Please select a start date");
      return false;
    }
    if (days < 0.5) {
      setError("Minimum leave duration is 0.5 days");
      return false;
    }
    return true;
  };

  useEffect(() => {
    setDays(0)
    const days = calculateDaysOfLeave(
      startDate,
      startDateIsAm,
      endDate,
      endDateIsAm
    );
    setDays(days);
  }, [startDate, endDate, startDateIsAm, endDateIsAm]);
  return (
    <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)} >
      <DialogContent
        hideClose={isLoading}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogTitle>Request for Leave</DialogTitle>
        <DialogDescription>
          Fill in the details of your leave request. Please note that you would
          have to request for unpaid leave if you have insufficient leave
          balance.
        </DialogDescription>
        <form
          className="w-full  flex flex-col max-h-[400px]"
          onSubmit={handleSubmit}
        >
          <div className="w-full overflow-y-auto flex flex-col gap-4">
            <LeaveTypeDropdown setSelectedLeaveType={setLeaveType} />
            <div className="flex gap-2 justify-between">
              <DatePicker
                label="Start Date"
                date={startDate}
                setDate={setStartDate}
              />
              <AmPmDropdown setIsAm={setStartDateIsAm} label="Start Time" />
            </div>

            <div className="flex gap-2 justify-between">
              <DatePicker
                label="End Date"
                date={endDate}
                setDate={setEndDate}
              />
              <AmPmDropdown setIsAm={setEndDateIsAm} label="End Time" />
            </div>

            <NumberInput
              label="Days"
              value={days}
              setValue={setDays}
              readOnly={true}
              classname="text-gray-500"
            />
            <div className="text-sm text-gray-500 mb-2 italic">
              Number of days will be calculated automatically for you.
            </div>

            <MultiLineTextInput
              label="Reason"
              value={reason}
              setValue={setReason}
              placeholder="Enter reason for leave"
            />
            <FileUploadInput label={file ? "Selected Image" : "Upload Image"} setFile={setFile} file={file} description="Attach relevant document to support your leave request." />
            <div className="text-red-500 text-sm">{error}</div>
            <Button type="submit" disabled={isLoading || error.length > 0}>
              {!isLoading ? (
                "Create Request"
              ) : (
                <Loader2 className="animate-spin" />
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog >
  );
}

export default RequestLeaveDialog;
